import { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Loader2 } from 'lucide-react';

// Fix Leaflet default icon paths for Vite bundler
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

const markerIcon = new L.Icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

interface ClientMapProps {
  indirizzo?: string;
  citta?: string;
  provincia?: string;
  cap?: string;
  latitudine?: number | null;
  longitudine?: number | null;
  isGeocoding?: boolean;
}

export default function ClientMap({ indirizzo, citta, provincia, cap, latitudine, longitudine, isGeocoding }: ClientMapProps) {
  const fullAddress = useMemo(() => {
    const parts = [indirizzo, cap, citta, provincia ? `(${provincia})` : null, 'Italia']
      .filter(Boolean);
    return parts.length > 1 ? parts.join(', ') : '';
  }, [indirizzo, citta, provincia, cap]);

  const hasCoords = latitudine != null && longitudine != null;

  if (!fullAddress && !hasCoords) {
    return (
      <div className="bg-gray-50 rounded-lg h-[200px] flex flex-col items-center justify-center text-gray-400 gap-2">
        <MapPin className="h-8 w-8" />
        <span className="text-sm">Indirizzo non disponibile</span>
      </div>
    );
  }

  if (isGeocoding) {
    return (
      <div className="bg-gray-50 rounded-lg h-[200px] flex flex-col items-center justify-center text-gray-400 gap-2">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-xs">Localizzazione in corso...</span>
      </div>
    );
  }

  if (!hasCoords) {
    return (
      <div className="bg-gray-50 rounded-lg h-[200px] flex flex-col items-center justify-center text-gray-400 gap-2">
        <MapPin className="h-8 w-8" />
        <span className="text-sm">Posizione non trovata</span>
        <span className="text-xs">{fullAddress}</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden h-[200px]">
      <MapContainer
        center={[latitudine!, longitudine!]}
        zoom={12}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[latitudine!, longitudine!]} icon={markerIcon}>
          <Popup>{fullAddress}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
}
